import { useState } from "react";
import { Mail, Phone, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useLanguage } from "@/contexts/LanguageContext";
import { submitContactForm, submitSubscription, ContactFormData } from "@/services/emailService";
import { toast } from "@/components/ui/use-toast";
import { useForm } from "react-hook-form";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import * as z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

const ContactSection = () => {
  const { t } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [subscribeEmail, setSubscribeEmail] = useState("");

  // Define form schema with Zod
  const formSchema = z.object({
    name: z.string().min(2, {
      message: "Name must be at least 2 characters.",
    }),
    email: z.string().email({
      message: "Please enter a valid email address.",
    }),
    company: z.string().min(1, {
      message: "Company name is required.",
    }),
    interest: z.string(),
    message: z.string().min(10, {
      message: "Message must be at least 10 characters.",
    }),
  });

  // Initialize form with react-hook-form and zod resolver
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      company: "",
      interest: t("interest_option_consulting"),
      message: "",
    },
  });

  // Form submission handler
  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    setIsSubmitting(true);
    try {
      // Convert the form values to ContactFormData to ensure all required fields are present
      const contactData: ContactFormData = {
        name: values.name,
        email: values.email,
        company: values.company,
        interest: values.interest,
        message: values.message
      };

      await submitContactForm(contactData);
      form.reset();
      toast({
        title: t("form_success"),
        description: t("form_success"),
      });
    } catch (error) {
      toast({
        title: t("form_error"),
        description: t("form_error"),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Newsletter subscription handler
  const handleSubscribe = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!subscribeEmail) return;

    setIsSubmitting(true);
    try {
      await submitSubscription({ email: subscribeEmail });
      setSubscribeEmail("");
      toast({
        title: t("form_success"),
        description: t("subscribe_success"),
      });
    } catch (error) {
      toast({
        title: t("form_error"),
        description: t("form_error"),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative overflow-hidden py-24 sm:py-28">
      <div className="absolute inset-0 z-0">
        <div className="absolute left-1/3 top-1/3 h-64 w-64 rounded-full bg-blue-500/8 blur-[120px] dark:bg-blue-600/10"></div>
        <div className="absolute bottom-1/3 right-1/3 h-64 w-64 rounded-full bg-emerald-500/8 blur-[120px] dark:bg-emerald-600/10"></div>
      </div>

      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-14 text-center">
          <h2 className="section-heading mb-6">
            {t("contact_title")}
          </h2>
          <p className="section-subtitle mx-auto max-w-3xl">
            {t("contact_subtitle")}
          </p>
        </div>

        <div className="mx-auto grid max-w-6xl items-start gap-8 md:grid-cols-2">
          <div>
            <h3 className="mb-8 text-3xl font-semibold text-slate-900 dark:text-white">{t("contact_info")}</h3>

            <div className="space-y-6">
              <div className="flex items-start rounded-2xl border border-slate-200/70 bg-white/70 p-5 shadow-sm backdrop-blur dark:border-slate-800/70 dark:bg-slate-950/70">
                <div className="mr-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-100 text-blue-600 dark:bg-blue-950/40 dark:text-blue-400">
                  <Mail className="h-6 w-6" />
                </div>
                <div>
                  <h4 className="mb-1 text-lg font-semibold text-slate-900 dark:text-white">{t("email_us")}</h4>
                  <p className="text-slate-600 dark:text-slate-400">info@inbloqs.com</p>
                </div>
              </div>

              <div className="flex items-start rounded-2xl border border-slate-200/70 bg-white/70 p-5 shadow-sm backdrop-blur dark:border-slate-800/70 dark:bg-slate-950/70">
                <div className="mr-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-400">
                  <Phone className="h-6 w-6" />
                </div>
                <div>
                  <h4 className="mb-1 text-lg font-semibold text-slate-900 dark:text-white">{t("call_us")}</h4>
                  <p className="text-slate-600 dark:text-slate-400">+52 331 319 5542</p>
                </div>
              </div>

              <div className="flex items-start rounded-2xl border border-slate-200/70 bg-white/70 p-5 shadow-sm backdrop-blur dark:border-slate-800/70 dark:bg-slate-950/70">
                <div className="mr-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-700 dark:bg-slate-900 dark:text-slate-300">
                  <MapPin className="h-6 w-6" />
                </div>
                <div>
                  <h4 className="mb-1 text-lg font-semibold text-slate-900 dark:text-white">{t("location")}</h4>
                  <p className="text-slate-600 dark:text-slate-400">Guadalajara, México</p>
                </div>
              </div>
            </div>

            <div className="mt-8 rounded-[1.5rem] border border-slate-200/70 bg-white/70 p-8 shadow-[0_16px_45px_-24px_rgba(15,23,42,0.3)] backdrop-blur dark:border-slate-800/70 dark:bg-slate-950/70">
              <h4 className="mb-3 text-xl font-semibold text-slate-900 dark:text-white">{t("join_community")}</h4>
              <p className="mb-6 text-slate-600 dark:text-slate-400">
                {t("newsletter_desc")}
              </p>

              <form className="flex flex-col gap-3 sm:flex-row" onSubmit={handleSubscribe}>
                <Input
                  type="email"
                  placeholder={t("your_email")}
                  className="border-slate-200 bg-white/90 focus:border-blue-500 dark:border-slate-800 dark:bg-slate-950/70"
                  value={subscribeEmail}
                  onChange={(e) => setSubscribeEmail(e.target.value)}
                  required
                />
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="rounded-full bg-blue-600 text-white transition-all duration-300 hover:bg-blue-700"
                >
                  {t("subscribe")}
                </Button>
              </form>
            </div>
          </div>

          <div className="rounded-[1.5rem] border border-slate-200/70 bg-white/70 p-8 shadow-[0_16px_45px_-24px_rgba(15,23,42,0.3)] backdrop-blur dark:border-slate-800/70 dark:bg-slate-950/70">
            <h3 className="mb-8 text-2xl font-semibold text-slate-900 dark:text-white">{t("request_demo_form")}</h3>

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                <div className="grid gap-6 sm:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-slate-900 dark:text-white">{t("full_name")}</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="John Doe"
                            className="border-slate-200 bg-white/90 focus:border-blue-500 dark:border-slate-800 dark:bg-slate-950/70"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-slate-900 dark:text-white">{t("email_address")}</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="john@company.com"
                            className="border-slate-200 bg-white/90 focus:border-blue-500 dark:border-slate-800 dark:bg-slate-950/70"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="company"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-slate-900 dark:text-white">{t("company")}</FormLabel>
                        <FormControl>
                          <Input
                            placeholder={t("your_company")}
                            className="border-slate-200 bg-white/90 focus:border-blue-500 dark:border-slate-800 dark:bg-slate-950/70"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="interest"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-slate-900 dark:text-white">{t("interest_area")}</FormLabel>
                        <FormControl>
                          <select
                            className="w-full rounded-md border border-slate-200 bg-white/90 px-3 py-2 text-sm ring-offset-background focus:border-blue-500 dark:border-slate-800 dark:bg-slate-950/70"
                            {...field}
                          >
                            <option>{t("interest_option_private_ai")}</option>
                            <option>{t("interest_option_automation")}</option>
                            <option>{t("interest_option_consulting")}</option>
                            <option>{t("interest_option_platform")}</option>
                            <option>{t("interest_option_other")}</option>
                          </select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="message"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-slate-900 dark:text-white">{t("message")}</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder={t("message_placeholder")}
                          rows={4}
                          className="border-slate-200 bg-white/90 focus:border-blue-500 dark:border-slate-800 dark:bg-slate-950/70"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button
                  type="submit"
                  className="w-full rounded-full bg-blue-600 text-white transition-all duration-300 hover:bg-blue-700"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? t("sending") : t("request_demo")}
                </Button>
              </form>
            </Form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;

